import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Plus, MapPin, Pencil, Trash2, X, Users, HeartPulse, Home,
  CheckCircle2, TrendingUp, AlertCircle, Sparkles, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", district: "", state: "", population: "",
  phc_name: "", distance_from_college_km: "", notes: "",
};

const STATUS_META = {
  complete:            { label: "100% Covered",    color: "#1F4A3D", bg: "#D9ECDF", Icon: CheckCircle2 },
  on_track:            { label: "On Track",        color: "#367661", bg: "#E3F1E8", Icon: TrendingUp },
  in_progress:         { label: "In Progress",     color: "#9a7536", bg: "#FDF1DE", Icon: Sparkles },
  not_started:         { label: "Not Started",     color: "#C75D4A", bg: "#FDE0DC", Icon: AlertCircle },
  no_target:           { label: "Population Set — In Progress", color: "#9a7536", bg: "#FDF1DE", Icon: Sparkles },
  population_unknown:  { label: "Add Population",  color: "#5C5C55", bg: "#F2F0EB", Icon: AlertCircle },
};

export default function Villages() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/villages")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name || "", district: v.district || "", state: v.state || "",
      population: v.population ?? "", phc_name: v.phc_name || "",
      distance_from_college_km: v.distance_from_college_km ?? "", notes: v.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        population: form.population === "" ? null : parseInt(form.population, 10),
        distance_from_college_km: form.distance_from_college_km === "" ? null : parseFloat(form.distance_from_college_km),
      };
      if (editing) await api.put(`/villages/${editing.id}`, payload);
      else await api.post("/villages", payload);
      toast.success(editing ? "Village updated" : "Village added");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.name}?`)) return;
    try { await api.delete(`/villages/${v.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  // Aggregate KPIs across all villages
  const agg = items.reduce((a, v) => {
    a.pop += v.population || 0;
    a.surveyed += v.surveyed_population || 0;
    a.hh += v.households_visited || 0;
    a.patients += v.patients_identified || 0;
    a.completed += v.coverage_status === "complete" ? 1 : 0;
    return a;
  }, { pop: 0, surveyed: 0, hh: 0, patients: 0, completed: 0 });
  const totalCoverage = agg.pop > 0 ? Math.min(100, Math.round(agg.surveyed * 100 / agg.pop)) : 0;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Village Surveillance</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Villages &amp; Coverage</h1>
          <p className="text-brand-muted mt-1 text-sm">Live coverage &amp; disease profile — updates automatically as household visits are added.</p>
        </div>
        {isManager && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors" data-testid="add-village-btn">
            <Plus className="h-4 w-4" /> Add Village
          </button>
        )}
      </div>

      {/* Overall coverage strip */}
      {items.length > 0 && (
        <div className="rounded-xl border border-brand-border bg-white shadow-tactile p-5" data-testid="villages-overall">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-bold">Overall Population Coverage</div>
              <div className="mt-1 text-2xl font-heading font-semibold">
                {agg.surveyed.toLocaleString()} <span className="text-brand-muted text-base font-normal">/ {agg.pop.toLocaleString() || "—"} people surveyed</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <MiniKpi Icon={Home}       label="Households"          value={agg.hh} />
              <MiniKpi Icon={HeartPulse} label="Patients"            value={agg.patients} accent="#D78A76" />
              <MiniKpi Icon={CheckCircle2} label="100% covered"      value={`${agg.completed}/${items.length}`} accent="#1F4A3D" />
            </div>
          </div>
          <ProgressBar percent={totalCoverage} status={totalCoverage >= 100 ? "complete" : totalCoverage >= 60 ? "on_track" : totalCoverage > 0 ? "in_progress" : "not_started"} />
        </div>
      )}

      {loading ? <div className="text-brand-muted">Loading…</div> : items.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-brand-border bg-white p-12 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-brand-surfaceAlt grid place-items-center"><MapPin className="h-7 w-7 text-brand-primary" /></div>
          <h3 className="font-heading text-xl font-semibold mt-4">No villages yet</h3>
          <p className="text-brand-muted mt-2 text-sm">Add the first village your team will visit.</p>
          {isManager && (
            <button onClick={openCreate} className="inline-flex mt-6 items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid="empty-add-village-btn">
              <Plus className="h-4 w-4" /> Add Village
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => <VillageCard key={v.id || v.name} v={v} isManager={isManager} onEdit={openEdit} onRemove={remove} onOpen={setDrill} />)}
        </div>
      )}

      {drill && <VillageDrill v={drill} onClose={() => setDrill(null)} />}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-xl p-6" data-testid="village-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold">{editing ? "Edit Village" : "Add Village"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Village Name *"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="v-name" /></Field>
              <Field label="District"><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="v-district" /></Field>
              <Field label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="v-state" /></Field>
              <Field label="Population (whole village)"><Input type="number" value={form.population} onChange={(e) => setForm({ ...form, population: e.target.value })} data-testid="v-pop" /></Field>
              <Field label="PHC Name"><Input value={form.phc_name} onChange={(e) => setForm({ ...form, phc_name: e.target.value })} data-testid="v-phc" /></Field>
              <Field label="Distance from College (km)"><Input type="number" step="0.1" value={form.distance_from_college_km} onChange={(e) => setForm({ ...form, distance_from_college_km: e.target.value })} data-testid="v-distance" /></Field>
              <Field label="Notes" className="md:col-span-2">
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none resize-y" data-testid="v-notes" />
              </Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
              <button type="submit" disabled={saving} className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium disabled:opacity-60" data-testid="v-submit">
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* =============== VILLAGE CARD =============== */
function VillageCard({ v, isManager, onEdit, onRemove, onOpen }) {
  const meta = STATUS_META[v.coverage_status] || STATUS_META.population_unknown;
  const StatusIcon = meta.Icon;
  const remaining = (v.population || 0) - (v.surveyed_population || 0);

  return (
    <div className="bg-white border border-brand-border rounded-xl p-5 hover:border-brand-primary transition-colors flex flex-col" data-testid={`village-card-${v.name}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-brand-muted font-bold">{[v.district, v.state].filter(Boolean).join(", ") || "Location"}</div>
          <h3 className="font-heading text-lg font-semibold mt-0.5 truncate">{v.name}</h3>
        </div>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] uppercase tracking-wider font-bold shrink-0"
          style={{ background: meta.bg, color: meta.color }}
          data-testid={`village-status-${v.name}`}
        >
          <StatusIcon className="h-3 w-3" /> {meta.label}
        </span>
      </div>

      {/* Coverage bar */}
      <div className="mt-4">
        {v.coverage_percent != null ? (
          <>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider text-brand-muted font-bold">Population Coverage</span>
              <span className="text-sm font-heading font-semibold tabular-nums" style={{ color: meta.color }}>{v.coverage_percent}%</span>
            </div>
            <ProgressBar percent={v.coverage_percent} status={v.coverage_status} />
            <div className="mt-1 text-[11px] text-brand-muted flex justify-between">
              <span>{(v.surveyed_population || 0).toLocaleString()} surveyed</span>
              <span>{(v.population || 0).toLocaleString()} target</span>
            </div>
            {remaining > 0 && v.coverage_status !== "complete" && (
              <div className="text-[11px] text-brand-muted mt-0.5">~{remaining.toLocaleString()} people remaining</div>
            )}
          </>
        ) : (
          <div className="text-xs text-brand-muted italic">Set village population to enable coverage tracking.</div>
        )}
      </div>

      {/* Stat chips */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <Chip Icon={Home}       value={v.households_visited || 0} label="Households" />
        <Chip Icon={Users}      value={v.surveyed_population || 0} label="People" />
        <Chip Icon={HeartPulse} value={v.patients_identified || 0} label="Patients" accent="#D78A76" />
      </div>

      {/* Gender split (mini bars) */}
      {(v.surveyed_population || 0) > 0 && <MiniGenderBar gender={v.gender_breakdown} />}

      {/* Top disease chip */}
      {(v.disease_breakdown || []).length > 0 && (
        <div className="mt-3 rounded-md bg-brand-accent/10 border border-brand-accent/20 p-2">
          <div className="text-[10px] uppercase tracking-wider text-[#9C5240] font-bold">Top Health Finding</div>
          <div className="text-sm mt-0.5 font-medium truncate">
            {v.disease_breakdown[0].condition}
            <span className="text-brand-muted font-normal"> · {v.disease_breakdown[0].count} case{v.disease_breakdown[0].count === 1 ? "" : "s"}</span>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="mt-4 pt-4 border-t border-brand-border flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(v)}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-brand-primary text-white hover:bg-brand-primaryHover text-xs font-bold"
          data-testid={`view-village-${v.name}`}
        >
          <BarChart3 className="h-3.5 w-3.5" /> Analytics
        </button>
        {isManager && (
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit(v)} className="h-8 w-8 rounded-md hover:bg-brand-surfaceAlt grid place-items-center" data-testid={`edit-village-${v.name}`}>
              <Pencil className="h-3.5 w-3.5 text-brand-muted" />
            </button>
            <button onClick={() => onRemove(v)} className="h-8 w-8 rounded-md hover:bg-brand-surfaceAlt grid place-items-center" data-testid={`delete-village-${v.name}`}>
              <Trash2 className="h-3.5 w-3.5 text-brand-danger" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* =============== DRILL-DOWN MODAL =============== */
function VillageDrill({ v, onClose }) {
  const meta = STATUS_META[v.coverage_status] || STATUS_META.population_unknown;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-brand-border w-full max-w-3xl p-6 my-8" data-testid="village-drill">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">{[v.district, v.state].filter(Boolean).join(", ") || "Village"}</div>
            <h2 className="font-heading text-2xl font-semibold mt-1">{v.name}</h2>
            {v.phc_name && <div className="text-xs text-brand-muted mt-0.5">PHC: {v.phc_name}{v.distance_from_college_km != null ? ` · ${v.distance_from_college_km} km from college` : ""}</div>}
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
        </div>

        {/* Coverage hero */}
        <div className="mt-5 rounded-lg p-4" style={{ background: meta.bg }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.color }}>{meta.label}</div>
              <div className="text-3xl font-heading font-semibold mt-1 tabular-nums" style={{ color: meta.color }}>
                {v.coverage_percent != null ? `${v.coverage_percent}%` : "—"}
              </div>
              <div className="text-xs mt-1" style={{ color: meta.color }}>
                {(v.surveyed_population || 0).toLocaleString()} of {(v.population || 0).toLocaleString() || "?"} people covered
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.color }}>Households</div>
              <div className="text-3xl font-heading font-semibold tabular-nums" style={{ color: meta.color }}>{v.households_visited || 0}</div>
            </div>
          </div>
          {v.coverage_percent != null && (
            <div className="mt-3">
              <ProgressBar percent={v.coverage_percent} status={v.coverage_status} />
            </div>
          )}
        </div>

        {/* Demographics */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <DrillBlock title="Gender Split">
            {(v.surveyed_population || 0) === 0 ? <EmptyLine /> : (
              <div className="space-y-1.5">
                {Object.entries(v.gender_breakdown || {}).filter(([, c]) => c > 0).map(([k, c]) => {
                  const total = v.surveyed_population || 1;
                  const pct = Math.round(c * 100 / total);
                  const color = k === "Male" ? "#2B5F4E" : k === "Female" ? "#D78A76" : "#9CAB8E";
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="font-medium">{k}</span>
                        <span className="text-brand-muted tabular-nums">{c} · {pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-brand-surfaceAlt overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DrillBlock>

          <DrillBlock title="Age Groups">
            {(v.age_group_breakdown || []).length === 0 ? <EmptyLine /> : (
              <div className="space-y-1.5">
                {v.age_group_breakdown.slice(0, 6).map((row) => {
                  const total = v.surveyed_population || 1;
                  const pct = Math.round(row.count * 100 / total);
                  return (
                    <div key={row.group}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="font-medium">{row.group}</span>
                        <span className="text-brand-muted tabular-nums">{row.count} · {pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-brand-surfaceAlt overflow-hidden">
                        <div className="h-full rounded-full bg-brand-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DrillBlock>
        </div>

        {/* Disease profile */}
        <div className="mt-4">
          <DrillBlock title="Disease &amp; Health Findings">
            {(v.disease_breakdown || []).length === 0 ? <EmptyLine label="No health findings recorded yet." /> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {v.disease_breakdown.slice(0, 12).map((row) => (
                  <div key={row.condition} className="flex items-center justify-between rounded-md border border-brand-border bg-brand-surfaceAlt/60 px-3 py-2">
                    <span className="text-sm font-medium truncate">{row.condition}</span>
                    <span className="ml-2 shrink-0 inline-flex items-center rounded-full bg-brand-accent/15 text-[#9C5240] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                      {row.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </DrillBlock>
        </div>

        {v.notes && (
          <div className="mt-4 rounded-md border border-brand-border p-3">
            <div className="text-[10px] uppercase tracking-wider text-brand-muted font-bold mb-1">Notes</div>
            <div className="text-sm">{v.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =============== SMALL COMPONENTS =============== */
function ProgressBar({ percent, status }) {
  const meta = STATUS_META[status] || STATUS_META.in_progress;
  return (
    <div className="h-2.5 w-full rounded-full bg-brand-surfaceAlt overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, percent))}%`, background: meta.color }}
      />
    </div>
  );
}

function Chip({ Icon, value, label, accent = "#2B5F4E" }) {
  return (
    <div className="rounded-md border border-brand-border bg-brand-surfaceAlt/50 px-2 py-1.5">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-brand-muted font-bold">
        <Icon className="h-3 w-3" style={{ color: accent }} /> {label}
      </div>
      <div className="text-base font-heading font-semibold tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function MiniKpi({ Icon, label, value, accent = "#2B5F4E" }) {
  return (
    <div className="text-right">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-brand-muted font-bold justify-end">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-lg font-heading font-semibold tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function MiniGenderBar({ gender }) {
  const total = Object.values(gender || {}).reduce((a, b) => a + b, 0);
  if (!total) return null;
  const male = gender.Male || 0, female = gender.Female || 0, other = gender.Other || 0;
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-brand-muted font-bold mb-1">Gender Split</div>
      <div className="h-2 flex rounded-full overflow-hidden bg-brand-surfaceAlt">
        {male   > 0 && <div style={{ width: `${male * 100 / total}%`,   background: "#2B5F4E" }} />}
        {female > 0 && <div style={{ width: `${female * 100 / total}%`, background: "#D78A76" }} />}
        {other  > 0 && <div style={{ width: `${other * 100 / total}%`,  background: "#9CAB8E" }} />}
      </div>
      <div className="mt-1 flex gap-3 text-[10px] text-brand-muted">
        {male   > 0 && <span><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: "#2B5F4E" }} />♂ {male}</span>}
        {female > 0 && <span><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: "#D78A76" }} />♀ {female}</span>}
        {other  > 0 && <span><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: "#9CAB8E" }} />○ {other}</span>}
      </div>
    </div>
  );
}

function DrillBlock({ title, children }) {
  return (
    <div className="rounded-xl border border-brand-border p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-brand-muted font-bold mb-3">{title}</div>
      {children}
    </div>
  );
}

function EmptyLine({ label = "No data yet." }) {
  return <div className="text-xs text-brand-muted italic">{label}</div>;
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Input(props) {
  return <input {...props} className="w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" />;
}
